import { DeepMap, DeepPartial, FieldError, FieldErrors } from 'react-hook-form';

import { useCmsContent } from '@src/contexts/cms-content-context';
import { Commons2 } from '@src/generated/dato-cms';

type CmsContent = Commons2['tripManagerContent'];
type Message = keyof NonNullable<CmsContent>;

type FieldErrorMessages<T> = DeepMap<DeepPartial<T>, string>;

const isFieldErrors = (error: unknown): error is FieldErrors =>
  !!error && typeof error === 'object' && !('type' in error);
const isFieldError = (error: unknown): error is FieldError => !!error && typeof error === 'object' && 'type' in error;

const getErrorMessages = <T extends Record<string, unknown>>(
  errors: FieldErrors<T>,
  content: NonNullable<CmsContent>,
): FieldErrorMessages<T> => {
  return Object.fromEntries(
    Object.entries(errors).map(([fieldKey, error]) => {
      if (isFieldErrors(error)) {
        return [fieldKey, getErrorMessages(error, content)];
      }

      if (isFieldError(error)) {
        const errorMessage = error.message as Message;

        return [fieldKey, content[errorMessage]];
      }

      return [fieldKey, undefined];
    }),
  ) as FieldErrorMessages<T>;
};

export const useErrorMessages = <T extends Record<string, unknown>>(errors: FieldErrors<T>): FieldErrorMessages<T> => {
  const content = useCmsContent();

  return getErrorMessages(errors, content);
};
